import {
  WppActionButton,
  WppIconLockOn,
  WppTag,
  WppTypography,
  WppIconEyeOn,
  WppIconHome,
  WppIconMore,
  WppMenuContext,
  WppListItem,
  WppTooltip,
} from '@platform-ui-kit/components-library-react'
import { UserDetails } from '@wpp-open/core'
import clsx from 'clsx'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetState } from 'react-use'

import { useUpdateUsersPreferredHubApi } from 'api/groups/mutations/useUpdateUsersPreferredHubApi'
import { is5xxError } from 'api/utils'
import { AvatarWithNameCell } from 'components/cellRenderers/avatarWithName/AvatarWithNameCell'
import { AvatarWithInfoSkeleton } from 'components/cellRenderers/skeletons/avatarWithInfoSkeleton/AvatarWithInfoSkeleton'
import { Flex } from 'components/common/flex/Flex'
import { ColDef, TableInfinite } from 'components/common/table'
import { CriticalError, RenderErrorBoundary, RenderErrorType } from 'components/renderError'
import { SvgNoRecords } from 'components/svg/network/SvgNoRecords'
import { Permission } from 'constants/permission'
import { TableKey } from 'constants/table'
import { useGetHubHierarchy } from 'hooks/useGetHubHierarchy'
import { useHasPermission } from 'hooks/useHasPermission'
import {
  handleReloadUserByMail,
  isCurrentDefaultHub,
  useUserHubsLoader,
  useLayerAdminHubs,
} from 'pages/admin/members/viewMemberSideModal/utils'
import styles from 'pages/admin/members/viewMemberSideModal/ViewMemberSideModal.module.scss'
import { useCurrentTenantData } from 'providers/currentTenantData/CurrentTenantDataContext'
import { useOtherTenantsAndUserData } from 'providers/otherTenantsAndUserData/OtherTenantsAndUserDataContext'
import { useToast } from 'providers/toast/ToastProvider'
import { UserHub, HubType } from 'types/hubs/hubs'
import { routesManager } from 'utils/routesManager'

export interface State {
  isCriticalError?: boolean
}

interface Props {
  user: UserDetails
}

const NoRowsOverlayComponent = () => {
  const { t } = useTranslation()

  return (
    <Flex direction="column" align="center" gap={12} className={styles.notFound}>
      <SvgNoRecords />
      <WppTypography type="m-strong" data-testid="no-member-hubs-found-text">
        {t('os.members_list.view_member.hubs.placeholder')}
      </WppTypography>
    </Flex>
  )
}

export const ViewMemberHubsSection = ({ user }: Props) => {
  const { t } = useTranslation()
  const { enqueueToast } = useToast()
  const { loader } = useUserHubsLoader(user.email)
  const { getHubHierarchy } = useGetHubHierarchy()
  const { currentTenant } = useCurrentTenantData()
  const { userDetails } = useOtherTenantsAndUserData()
  const { hasPermission } = useHasPermission()
  const { mutateAsync: handleUpdateUsersPreferredHub, isLoading: isUpdatingUsersPrefferedHub } =
    useUpdateUsersPreferredHubApi()
  const layerAdminHubs = useLayerAdminHubs(userDetails.email, currentTenant.id)

  const isCurrentUser = userDetails?.email === user.email
  const [{ isCriticalError }, setState] = useSetState<State>({
    isCriticalError: false,
  })

  const updatePreferredHub = useCallback(
    async (hubId: string) => {
      const { email: userEmail } = user
      await handleUpdateUsersPreferredHub({
        userPreferredHub: [
          {
            userEmail,
            hubId,
          },
        ],
      })
      handleReloadUserByMail(userEmail, isCurrentUser)
      enqueueToast({
        message: t('os.hubs.members.toasts.default_hub_toast'),
        type: 'success',
      })
    },
    [enqueueToast, handleUpdateUsersPreferredHub, isCurrentUser, t, user],
  )

  const columnDefs = useMemo<ColDef<UserHub>[]>(
    () => [
      {
        width: 350,
        colId: 'name',
        headerName: t('os.hubs.table.hub'),
        valueGetter: ({ data }) => data!.name,
        loadingCellRenderer: AvatarWithInfoSkeleton,
        cellRenderer: ({ data }) => {
          const { name, logoThumbnail, logoOriginal, id, type } = data!
          return (
            <Flex style={{ width: 300 }} align="center" gap={8}>
              <AvatarWithNameCell avatarThumbnail={logoThumbnail?.url || logoOriginal?.url || ''} userName={name} />
              {isCurrentDefaultHub(id, user, currentTenant) ? (
                <WppTag data-testid="default-hub" label={t('os.common.home')} variant="neutral" />
              ) : type === HubType.System ? (
                <WppTag variant="neutral" label={t('os.hubs.types.system')} />
              ) : null}
            </Flex>
          )
        },
        tooltipValueGetter: ({ data }) => data?.name,
      },
      {
        flex: 1,
        colId: 'hierarchy',
        headerName: t('os.hubs.table.hierarchy'),
        valueGetter: ({ data }) => {
          const hierarchy = getHubHierarchy(data!.organizationUnitId)
          return hierarchy === false ? (
            <Flex gap={8} align="center">
              <WppIconLockOn /> {t('os.hubs.no_access')}
            </Flex>
          ) : (
            hierarchy
          )
        },
      },

      {
        width: 100,
        colId: 'role',
        headerName: t('os.hubs.table.role'),
        valueGetter: ({ data }) => (data!.canManage ? t('os.hubs.table.admin') : t('os.hubs.table.viewer')),
      },
      {
        width: 100,
        colId: 'isActive',
        headerName: t('os.hubs.table.status'),
        valueGetter: ({ data }) => (data!.isActive ? t('os.hubs.table.active') : t('os.hubs.table.inactive')),
      },
      {
        width: 60,
        colId: 'actions',
        cellRenderer: ({ data }) => {
          const isLoading = isUpdatingUsersPrefferedHub
          const currentUserIsAdmin = layerAdminHubs?.data.find(layerAdminHub => layerAdminHub.id === data?.id)

          return data && currentUserIsAdmin ? (
            <WppMenuContext
              className={styles.moreMenu}
              dropdownConfig={{
                appendTo: () => document.body,
                placement: 'bottom-end',
              }}
              data-testid="member-menu-button"
            >
              <WppActionButton
                slot="trigger-element"
                loading={isLoading}
                disabled={isLoading}
                data-testid="trigger-more-menu"
              >
                <WppIconMore direction="horizontal" />
              </WppActionButton>

              <Flex direction="column" gap={4}>
                <WppListItem
                  linkConfig={{
                    target: '_blank',
                    rel: 'noreferrer',
                    href: `${
                      hasPermission(Permission.OS_ADMIN_SETTING_ACCESS)
                        ? routesManager.admin.hubs.url()
                        : routesManager.layerAdmin.hubs.url()
                    }?viewHubId=${data.id}`,
                  }}
                >
                  <WppIconEyeOn slot="left" />
                  <span slot="label">{t('os.hubs.table.view_member_modal.view_hub')}</span>
                </WppListItem>
                <WppListItem
                  disabled={isCurrentDefaultHub(data.id!, user, currentTenant)}
                  onWppChangeListItem={() => updatePreferredHub(data?.id!)}
                  data-testid="set-as-home-button"
                >
                  <WppIconHome slot="left" />
                  <span slot="label">{t('os.hubs.table.view_member_modal.set_hub_as_home')}</span>
                </WppListItem>
              </Flex>
            </WppMenuContext>
          ) : (
            <WppTooltip
              className={styles.iconLock}
              config={{ placement: 'top' }}
              slot="right"
              text={t('os.hubs.table.view_member_modal.no_access')}
            >
              <WppIconLockOn />
            </WppTooltip>
          )
        },
      },
    ],
    [
      t,
      user,
      currentTenant,
      getHubHierarchy,
      isUpdatingUsersPrefferedHub,
      layerAdminHubs?.data,
      hasPermission,
      updatePreferredHub,
    ],
  )

  return (
    <Flex direction="column" gap={12}>
      {isCriticalError && <CriticalError />}
      <RenderErrorBoundary catchErrors={[RenderErrorType.DataIsNotAvailable]}>
        <Flex direction="column" gap={12} data-testid="hubs-header-section">
          <TableInfinite
            tableKey={TableKey.VIEW_MEMBER_HUBS}
            className={clsx('member-hubs', styles.table)}
            columnDefs={columnDefs}
            noRowsOverlayComponent={NoRowsOverlayComponent}
            loader={loader}
            onLoadError={error => {
              if (is5xxError(error)) {
                setState({ isCriticalError: true })
              }
            }}
          />
        </Flex>
      </RenderErrorBoundary>
    </Flex>
  )
}
